import React from 'react';
import { X } from 'lucide-react';

type TemplatePlaceholdersHelpModalProps = {
  open: boolean;
  onClose: () => void;
};

export function TemplatePlaceholdersHelpModal({ open, onClose }: TemplatePlaceholdersHelpModalProps) {
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/40"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="w-full max-w-lg max-h-[85vh] flex flex-col rounded-xl border border-app-border bg-app-surface shadow-xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="placeholders-help-title"
      >
        <div className="shrink-0 px-5 py-4 border-b border-app-border flex items-center justify-between gap-3">
          <div>
            <h2 id="placeholders-help-title" className="text-base font-semibold text-app-text">
              Vorlagen &amp; E-Signatur
            </h2>
            <p className="text-xs text-app-text-subtle mt-0.5">Platzhalter in Word und Signaturfelder</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded text-app-text-subtle transition-colors hover:bg-app-hover hover:text-app-text-muted"
            aria-label="Schließen"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="app-scrollbar flex-1 space-y-5 overflow-y-auto overscroll-contain px-5 py-4 text-sm leading-relaxed text-app-text-muted">
          <p>
            Markiere in Word die Stellen, die später mit deinen Eingaben befüllt werden sollen. Beim Öffnen der Vorlage
            erkennt die App diese Markierungen und zeigt dafür Felder an - daraus entsteht ein PDF, das du herunterladen
            oder zur elektronischen Unterschrift senden kannst.
          </p>

          <div className="rounded-lg border border-app-border bg-app-surface-muted px-4 py-3">
            <p className="text-xs font-medium text-app-text-muted mb-2">Grundsyntax</p>
            <p className="font-mono text-sm text-app-text">{'{d.feldname}'}</p>
            <p className="text-xs text-app-text-muted mt-2">
              Das Präfix <span className="font-mono">d.</span> ist fest vorgegeben. Alles danach ist der Name deines Feldes.
            </p>
          </div>

          <section>
            <h3 className="font-semibold text-app-text mb-2">Signaturfeld</h3>
            <p className="mb-2">
              Für E-Signatur per SignWell setze in Word ein Signaturfeld mit dem Formatter{' '}
              <code className="text-xs bg-app-surface border border-app-border px-1.5 py-0.5 rounded font-mono">:sign</code>:
            </p>
            <p className="font-mono text-sm text-app-text bg-app-surface-muted border border-app-border rounded px-3 py-2">
              {'{d.unterschrift:sign}'}
            </p>
            <p className="text-xs text-app-text-muted mt-2">
              Das erscheint als Signaturstelle im PDF, nicht als Textfeld in der Eingabemaske. Name und E-Mail des
              Unterzeichners gibst du beim Senden ein.
            </p>
          </section>

          <section>
            <h3 className="font-semibold text-app-text mb-2">Beispiele</h3>
            <ul className="space-y-2">
              <li className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                <code className="text-xs bg-app-surface border border-app-border px-1.5 py-0.5 rounded font-mono">{'{d.kunde}'}</code>
                <span className="text-app-text-muted">ein Feld „Kunde“</span>
              </li>
              <li className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                <code className="text-xs bg-app-surface border border-app-border px-1.5 py-0.5 rounded font-mono">{'{d.kunde.name}'}</code>
                <span className="text-app-text-muted">gruppiert als Kunde → Name in der Maske</span>
              </li>
              <li className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                <code className="text-xs bg-app-surface border border-app-border px-1.5 py-0.5 rounded font-mono">{'{d.datum:date(DD.MM.YYYY)}'}</code>
                <span className="text-app-text-muted">mit Datumsformat - Feld heißt trotzdem <span className="font-mono">d.datum</span></span>
              </li>
              <li className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                <code className="text-xs bg-app-surface border border-app-border px-1.5 py-0.5 rounded font-mono">{'{d.unterschrift:sign}'}</code>
                <span className="text-app-text-muted">Signaturfeld für E-Signatur</span>
              </li>
              <li className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                <code className="text-xs bg-app-surface border border-app-border px-1.5 py-0.5 rounded font-mono">{'{d.datum_unterschrift:sign}'}</code>
                <span className="text-app-text-muted">optionales Datumsfeld an der Signaturstelle</span>
              </li>
            </ul>
          </section>

          <section>
            <h3 className="font-semibold text-app-text mb-1.5">Was in der Eingabemaske erscheint</h3>
            <p>
              Nur Platzhalter im Format <code className="text-xs bg-app-surface-muted px-1 py-0.5 rounded font-mono">{'{d.…}'}</code>{' '}
              ohne <code className="text-xs bg-app-surface-muted px-1 py-0.5 rounded font-mono">:sign</code> werden als Felder geladen.
              Signatur-Tags werden nicht in der Maske angezeigt - der Empfänger wird beim Senden abgefragt.
            </p>
          </section>

          <section>
            <h3 className="font-semibold text-app-text mb-1.5">Ablauf</h3>
            <ol className="list-decimal pl-5 space-y-1.5 text-app-text-muted">
              <li>DOCX mit Platzhaltern hochladen (ggf. mit <span className="font-mono text-xs">:sign</span>)</li>
              <li>Vorlage in der Liste öffnen</li>
              <li>Werte eintragen und „Dokument fertigstellen“</li>
              <li>PDF erstellen</li>
              <li>Herunterladen oder „Zur Unterschrift senden“</li>
              <li>Status unter „Unterschriften“ verfolgen</li>
            </ol>
          </section>

          <section>
            <h3 className="font-semibold text-app-text mb-1.5">Tipps für Word</h3>
            <ul className="list-disc pl-5 space-y-1 text-app-text-muted">
              <li>Platzhalter in einem Stück tippen, nicht Buchstabe für Buchstabe formatieren</li>
              <li>Kurze Namen ohne Leerzeichen, z.&nbsp;B. <span className="font-mono text-xs">kunde_name</span></li>
              <li>Datei als <strong>.docx</strong> speichern</li>
              <li>Für <span className="font-mono text-xs">:sign</span> Schriftart Arial oder Calibri verwenden</li>
              <li>Öffnende <span className="font-mono">{'{'}</span> unsichtbar machen (Hintergrundfarbe), damit keine technische Markierung sichtbar bleibt</li>
              <li>Pro Feldname nur ein <span className="font-mono text-xs">:sign</span>-Tag verwenden</li>
            </ul>
          </section>

          <p className="text-xs text-app-text-subtle pt-1 border-t border-app-border">
            Mehr zu Tags:{' '}
            <a
              href="https://carbone.io/documentation.html"
              target="_blank"
              rel="noopener noreferrer"
              className="text-pipedrive-blue hover:underline"
            >
              Carbone-Dokumentation
            </a>
            {' · '}
            <a
              href="https://carbone.io/documentation/design/advanced-features/signatures.html"
              target="_blank"
              rel="noopener noreferrer"
              className="text-pipedrive-blue hover:underline"
            >
              Carbone Signaturfelder
            </a>
          </p>
        </div>

        <div className="shrink-0 px-5 py-4 border-t border-app-border flex justify-end">
          <button type="button" onClick={onClose} className="btn-pipedrive-secondary">
            Verstanden
          </button>
        </div>
      </div>
    </div>
  );
}
